import React, { useState } from 'react';
import { applyPhoneMask } from '../utils/masks';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LegalDocsModal } from './LegalDocsModal';
import brandLogo from '../assets/techtennis-logo.png';
import atpLogo from '../assets/atp-tour-logo.png';
import bgImage from '../assets/miami-open-ernesto.jpg';

export const LoginView = () => {
  const { login, registerClient, registerProfessor } = useAuth();
  
  const [mode, setMode] = useState<'login' | 'register_client' | 'register_prof' | 'forgot_password'>('login');
  
  // Login State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Register State
  const [regName, setRegName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [isCompetitionManager, setIsCompetitionManager] = useState(false);
  
  const [successMsg, setSuccessMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Legal Modal State
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalTab, setLegalModalTab] = useState<'privacy'|'terms'>('privacy');
  const [lgpdAccepted, setLgpdAccepted] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');
    try {
      const res = await login(email, password);
      if (typeof res === 'string') {
        setLoginError(res);
      }
    } catch (err) {
      setLoginError('Erro ao realizar login.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lgpdAccepted) {
      setLoginError('Você precisa aceitar a Política de Privacidade e Termos de Uso para se cadastrar.');
      return;
    }
    setLoginError('');
    setSuccessMsg('');

    setIsLoading(true);
    const generatedPassword = '123456'; // Senha padrão inicial (será exigida a troca no primeiro acesso)
    const fullName = `${regName} ${regLastName}`.trim();
    try {
      if (mode === 'register_client') {
        await registerClient(fullName, email, generatedPassword, regPhone, isCompetitionManager);
        setSuccessMsg('Cadastro realizado com sucesso! Um e-mail de boas-vindas foi enviado para sua caixa de e-mail para você completar seu acesso.');
        setMode('login');
      } else {
        await registerProfessor(fullName, email, generatedPassword, regPhone, '', '');
        setSuccessMsg('Cadastro realizado com sucesso! Um e-mail de boas-vindas foi enviado para sua caixa de e-mail para você completar seu acesso.');
        setMode('login');
      }
      setLgpdAccepted(false);
    } catch (err: any) {
      setLoginError(err.message || 'Erro ao realizar cadastro');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setSuccessMsg('');
    if (!email) {
      setLoginError('Por favor, digite seu e-mail.');
      return;
    }
    
    setIsLoading(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'https://techtennis-api.vercel.app';
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Erro ao processar solicitação.');
      }
      setSuccessMsg(data.message || 'Se o e-mail existir, um link de recuperação foi enviado.');
    } catch (err: any) {
      setLoginError(err.message || 'Erro de conexão ao solicitar recuperação.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      backgroundImage: `url(${bgImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      position: 'relative',
      padding: '24px'
    }}>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,12,60,0.9) 0%, rgba(0,12,60,0.4) 100%)' }}></div>
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        style={{
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '24px',
          padding: '48px',
          width: '100%',
          maxWidth: '500px',
          position: 'relative',
          zIndex: 10,
          boxShadow: '0 24px 48px rgba(0,0,0,0.5)'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src={atpLogo} alt="ATP Tour Pro Stringer Store" style={{ height: '160px', objectFit: 'contain', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }} />
        </div>

        <AnimatePresence mode="wait">
          {mode === 'login' ? (
            <motion.form key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {successMsg && (
                <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid #10B981', color: '#34D399', padding: '12px', borderRadius: '8px', fontSize: '14px', textAlign: 'center' }}>
                  {successMsg}
                </div>
              )}
              {loginError && (
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #EF4444', color: '#F87171', padding: '12px', borderRadius: '8px', fontSize: '14px', textAlign: 'center' }}>
                  {loginError}
                </div>
              )}
              <div>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '8px' }}>E-mail</label>
                <input required type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>Senha</label>
                  <a href="#" onClick={(e) => { e.preventDefault(); setMode('forgot_password'); setLoginError(''); setSuccessMsg(''); }} style={{ color: 'var(--primary-color)', fontSize: '12px', textDecoration: 'none' }}>Esqueceu a senha?</a>
                </div>
                <div style={{ position: 'relative' }}>
                  <input required type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} style={{ ...inputStyle, paddingRight: '48px' }} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', top: '50%', right: '16px', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <button disabled={isLoading} type="submit" className="button-primary" style={{ padding: '16px', fontSize: '16px', fontWeight: 800, marginTop: '8px', background: 'var(--bg-gradient-top)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
                {isLoading ? 'Conectando...' : 'Acessar Plataforma'}
              </button>

              <div style={{ textAlign: 'center', marginTop: '16px', marginBottom: '8px' }}>
                <img src={brandLogo} alt="TechTennis" style={{ height: '90px', mixBlendMode: 'multiply', filter: 'brightness(0) invert(1)', opacity: 0.8 }} />
              </div>

              <div style={{ textAlign: 'center', marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '24px' }}>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '16px' }}>Ainda não tem acesso?</p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="button" onClick={() => { setMode('register_client'); setSuccessMsg(''); setLoginError(''); }} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', borderRadius: '8px', cursor: 'pointer' }}>
                    Sou Cliente
                  </button>
                  <button type="button" onClick={() => { setMode('register_prof'); setSuccessMsg(''); setLoginError(''); }} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', borderRadius: '8px', cursor: 'pointer' }}>
                    Sou Professor
                  </button>
                </div>
              </div>
            </motion.form>
          ) : mode === 'forgot_password' ? (
            <motion.form key="forgot_password" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onSubmit={handleForgotPassword} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <h2 style={{ color: 'white', fontSize: '20px', textAlign: 'center', marginBottom: '8px' }}>
                Recuperar Senha
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', textAlign: 'center', marginBottom: '8px' }}>
                Digite seu e-mail abaixo e enviaremos um link para criar uma nova senha.
              </p>
              {successMsg && (
                <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid #10B981', color: '#34D399', padding: '12px', borderRadius: '8px', fontSize: '14px', textAlign: 'center' }}>
                  {successMsg}
                </div>
              )}
              {loginError && (
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #EF4444', color: '#F87171', padding: '12px', borderRadius: '8px', fontSize: '14px', textAlign: 'center' }}>
                  {loginError}
                </div>
              )}
              <div>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '8px' }}>E-mail</label>
                <input required type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
              </div>
              
              <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                <button type="button" onClick={() => { setMode('login'); setLoginError(''); setSuccessMsg(''); }} style={{ flex: 1, padding: '14px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', borderRadius: '100px', cursor: 'pointer', fontWeight: 600 }}>
                  Voltar
                </button>
                <button type="submit" disabled={isLoading} className="button-primary" style={{ flex: 1, padding: '14px', background: 'var(--bg-gradient-top)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', cursor: isLoading ? 'not-allowed' : 'pointer' }}>
                  {isLoading ? 'Enviando...' : 'Enviar Link'}
                </button>
              </div>
            </motion.form>
          ) : (
            <motion.form key="register" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <h2 style={{ color: 'white', fontSize: '20px', textAlign: 'center', marginBottom: '16px' }}>
                Cadastro de {mode === 'register_client' ? 'Cliente' : 'Professor'}
              </h2>
              {successMsg && (
                <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid #10B981', color: '#34D399', padding: '12px', borderRadius: '8px', fontSize: '14px', textAlign: 'center' }}>
                  {successMsg}
                </div>
              )}
              {loginError && (
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #EF4444', color: '#F87171', padding: '12px', borderRadius: '8px', fontSize: '14px', textAlign: 'center' }}>
                  {loginError}
                </div>
              )}
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '8px' }}>Nome *</label>
                  <input required type="text" value={regName} onChange={e => setRegName(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '8px' }}>Sobrenome *</label>
                  <input required type="text" value={regLastName} onChange={e => setRegLastName(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '8px' }}>E-mail *</label>
                  <input required type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '8px' }}>Telefone *</label>
                  <input required type="text" value={regPhone} onChange={e => setRegPhone(applyPhoneMask(e.target.value))} style={inputStyle} />
                </div>



              </div>

              {mode === 'register_client' && (
                <div style={{ marginTop: '8px', fontSize: '14px', color: 'rgba(255,255,255,0.7)', textAlign: 'left', lineHeight: '1.4', display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <input 
                    type="checkbox" 
                    id="isCompetitionManager"
                    checked={isCompetitionManager}
                    onChange={(e) => setIsCompetitionManager(e.target.checked)}
                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                  />
                  <label htmlFor="isCompetitionManager" style={{ cursor: 'pointer', fontWeight: 500 }}>
                    Sou Gestor de Competição
                  </label>
                </div>
              )}

              <div style={{ marginTop: '8px', fontSize: '11px', color: 'rgba(255,255,255,0.7)', textAlign: 'left', lineHeight: '1.4', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <input 
                  type="checkbox" 
                  required
                  id="lgpdConsent"
                  checked={lgpdAccepted}
                  onChange={(e) => setLgpdAccepted(e.target.checked)}
                  style={{ marginTop: '2px', cursor: 'pointer' }}
                />
                <label htmlFor="lgpdConsent" style={{ cursor: 'pointer' }}>
                  Li e concordo com a <a href="#" onClick={(e) => { e.preventDefault(); setLegalModalTab('privacy'); setLegalModalOpen(true); }} style={{ color: 'var(--primary-color)' }}>Política de Privacidade</a> e <a href="#" onClick={(e) => { e.preventDefault(); setLegalModalTab('terms'); setLegalModalOpen(true); }} style={{ color: 'var(--primary-color)' }}>Termos de Uso</a>.
                </label>
              </div>

              <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                <button type="button" onClick={() => setMode('login')} style={{ flex: 1, padding: '14px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', borderRadius: '100px', cursor: 'pointer', fontWeight: 600 }}>
                  Voltar
                </button>
                <button type="submit" className="button-primary" style={{ flex: 1, padding: '14px', background: 'var(--bg-gradient-top)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
                  Concluir Cadastro
                </button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </motion.div>

      <LegalDocsModal 
        isOpen={legalModalOpen} 
        onClose={() => setLegalModalOpen(false)} 
        defaultTab={legalModalTab} 
      />
    </div>
  );
};

const inputStyle = { width: '100%', padding: '14px', borderRadius: '12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: '15px' };
